import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import EmailPopup from "@/components/EmailPopup";
import SubscribeForm from "@/components/SubscribeForm";
import { Squiggle, Star } from "@/components/Squiggle";
import { trackPageviewServer } from "@/lib/tracking";

export default function Landing() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    trackPageviewServer("");
    (async () => {
      try {
        const { data } = await api.get("/products");
        setProducts(data);
      } catch (_) {
        setProducts([]);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div className="min-h-screen bg-[#FFFDF9] text-[#1E1E24]" data-testid="landing-page">
      {/* Header */}
      <header className="max-w-7xl mx-auto flex items-center justify-between px-6 md:px-12 pt-8 pb-6">
        <Link to="/" className="flex items-center gap-2 group" data-testid="brand-logo">
          <span className="text-3xl" aria-hidden="true">🦡</span>
          <span className="font-headings text-2xl font-bold tracking-tight">
            Jungle <span className="text-[#FF5A5F]">Badger</span>
          </span>
        </Link>
        <nav className="flex items-center gap-4">
          <a
            href="https://www.junglebadger.com"
            target="_blank"
            rel="noopener noreferrer"
            className="hidden sm:inline-block font-bold text-sm text-[#1E1E24] hover:text-[#FF5A5F] transition-colors"
            data-testid="nav-main-site"
          >
            Main Site ↗
          </a>
          <Link
            to="/admin/login"
            className="text-xs md:text-sm font-bold text-[#5E5E66] hover:text-[#1E1E24] transition-colors"
            data-testid="nav-admin"
          >
            Admin
          </Link>
        </nav>
      </header>

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 pt-8 pb-16 md:pt-16 md:pb-24 relative">
        <Star className="absolute top-4 right-8 md:right-32 -rotate-12" size={44} color="#FFB400" />
        <Star className="absolute bottom-10 left-4 rotate-12" size={28} color="#FF5A5F" />

        <div className="max-w-4xl">
          <p className="inline-flex items-center gap-2 font-bold text-sm uppercase tracking-widest text-[#1E1E24] mb-6">
            <span className="w-8 h-0.5 bg-[#1E1E24] inline-block" /> Fresh finds, curated by the badger
          </p>
          <h1 className="font-headings text-5xl sm:text-6xl md:text-7xl font-bold tracking-tight leading-[1.05] mb-6">
            Our{" "}
            <span className="relative inline-block">
              <span className="text-[#FF5A5F]">curated</span>
              <Squiggle className="absolute -bottom-3 left-0 w-full" />
            </span>{" "}
            Amazon finds.
          </h1>
          <p className="text-lg md:text-xl text-[#5E5E66] max-w-2xl mb-8 leading-relaxed">
            Amazon deals straight to your inbox — hand-picked gadgets, gear
            and clever finds the badger has sniffed out.
          </p>
          <div className="max-w-lg mb-4">
            <SubscribeForm
              productContext="landing-hero"
              testIdPrefix="hero-subscribe"
              buttonLabel="Get deals"
            />
          </div>
          <div className="pt-2">
            <a
              href="#products"
              data-testid="hero-cta-primary"
              className="text-sm font-bold text-[#1E1E24] hover:text-[#FF5A5F] transition-colors underline underline-offset-4"
            >
              Or browse the deals ↓
            </a>
          </div>
        </div>
      </section>

      {/* Trust bar */}
      <section className="border-y-2 border-[#1E1E24] bg-[#F4F1EA]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-5 flex flex-wrap items-center justify-center gap-x-10 gap-y-3 text-sm font-bold uppercase tracking-widest text-[#1E1E24]">
          <span>🦡 6M+ Followers</span>
          <span className="hidden sm:inline">·</span>
          <span>✨ Curated Finds</span>
          <span className="hidden sm:inline">·</span>
          <span>🎁 Giveaways</span>
          <span className="hidden sm:inline">·</span>
          <span>⚡ Discount Codes</span>
        </div>
      </section>

      {/* Products */}
      <section id="products" className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24 relative" data-testid="products-section">
        <div className="mb-12 md:mb-16 flex items-end justify-between flex-wrap gap-4">
          <div>
            <h2 className="font-headings text-4xl md:text-5xl font-bold tracking-tight leading-tight">
              Badger picks
            </h2>
          </div>
          <p className="text-[#5E5E66] max-w-sm text-base">
            Tap any card to see the deal on Amazon.
          </p>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-12">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="h-96 rounded-3xl border-2 border-[#1E1E24]/20 bg-[#F4F1EA] animate-pulse" />
            ))}
          </div>
        ) : products.length === 0 ? (
          <div
            data-testid="products-empty"
            className="border-2 border-dashed border-[#1E1E24]/40 rounded-3xl p-12 text-center bg-white"
          >
            <div className="text-5xl mb-4">🦡</div>
            <h3 className="font-headings text-2xl font-bold mb-2">The badger is out sniffing...</h3>
            <p className="text-[#5E5E66]">
              Fresh deals are coming soon. Enter your email above to be the first to know.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-12">
            {products.map((p, i) => (
              <ProductCard key={p.id} product={p} index={i} />
            ))}
          </div>
        )}
      </section>

      {/* Bottom subscribe */}
      <section className="max-w-2xl mx-auto px-6 md:px-12 pb-16 md:pb-24 text-center">
        <h2 className="font-headings text-3xl md:text-4xl font-bold text-[#1E1E24] leading-tight mb-6">
          Amazon deals straight to your inbox
        </h2>
        <SubscribeForm
          productContext="landing-bottom"
          testIdPrefix="bottom-subscribe"
          buttonLabel="Subscribe"
        />
      </section>

      {/* Footer */}
      <footer className="border-t-2 border-[#1E1E24] bg-[#F4F1EA]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="flex items-center gap-2">
            <span className="text-2xl" aria-hidden="true">🦡</span>
            <span className="font-headings text-xl font-bold">
              Jungle <span className="text-[#FF5A5F]">Badger</span> Deals
            </span>
          </div>
          <p className="text-sm text-[#5E5E66]">
            © {new Date().getFullYear()} Jungle Badger. Curated with 🦡 & ✨.
          </p>
        </div>
      </footer>

      <EmailPopup context="landing" productContext="landing-popup" />
    </div>
  );
}
